// Talking to an agent from the office (the owner-agents prototype, "Ask the analyst" and the support
// chat): your questions, newest last, each with the agent's answer and the tool calls behind it.
// A question is queued and the worker answers it, so the screen checks back every couple of
// seconds until the answer is in. A stopped agent, or one without a model, says so instead.
// BL-189: under the place's head, each question a RowList row with its answer under it and its tool
// calls in an Area; the examples Links; the question box Composer (one mode).

import { useEffect, useState, type ReactNode } from 'react';
import { Area, Composer, Link, RowList, type Row } from '@flowology/ui-web/fl';
import { PageHead, type PageHeadProps } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { PartSays } from './agents-parts.tsx';
import { actionWords, clock } from './model.ts';
import type { AgentsProps } from './props.ts';
import type { ChatTurn } from './types.ts';

const POLL_MS = 1500;
const waiting = (t: ChatTurn) => t.status === 'queued' || t.status === 'running';

/** The tool calls behind an answer, one a row: what it called and how that went, its exact input. */
const toolRows = (t: ChatTurn): Row[] =>
  t.tools.map((c, i) => ({
    id: String(i),
    // the action in words, its dotted name on hover (F-04)
    name: (
      <>
        <span title={c.action ?? undefined}>{actionWords(c.action, c.actionTitle)}</span>{' '}
        {c.outcome.replace(/_/g, ' ')}
      </>
    ),
    lines: [{ text: <code className="agents-cmd">{JSON.stringify(c.input)}</code> }],
  }));

/** A question, a row: who asked and when, then the answer (or why there's none) and its tool calls. */
function turnRow(t: ChatTurn, timeZone?: string): Row {
  // the answer, or why there's none (its hook for the walks: .agents-answer, answered when it is one)
  const answer: ReactNode = (
    <span className="agents-answer" data-answered={t.answer && !waiting(t) ? '' : undefined}>
      {waiting(t) ? <span role="status">Working on it…</span> : (t.answer ?? t.stopReason ?? 'No answer.')}
    </span>
  );
  const n = t.tools.length;
  return {
    id: t.requestId,
    kicker: 'You',
    name: t.question,
    after: { text: ` · ${clock(t.askedAt, timeZone)}`, size: 'sm' },
    off: t.status === 'skipped' || t.status === 'failed',
    lines: [
      t.answer && !waiting(t) ? { text: answer, tone: 'ink' } : { text: answer },
      ...(n
        ? [
            {
              part: (
                <Area title={`${n} tool call${n === 1 ? '' : 's'} behind this answer`} level={2}>
                  <RowList label="The tool calls" rows={toolRows(t)} />
                </Area>
              ),
            },
          ]
        : []),
    ],
  };
}

export function Chat({
  props,
  head,
  said,
  title,
  intro,
  agentKey,
  examples,
}: {
  props: AgentsProps;
  /** The place's head over it (Inbox › Agents and approvals); left out, its own title and line (Help). */
  head?: PageHeadProps;
  /** What was just done, under the head. */
  said?: ReactNode;
  title?: string;
  intro?: string;
  agentKey: 'analyst' | 'support';
  examples: readonly string[];
}) {
  const chat = useQuery<{ turns: ChatTurn[] }>('agents.chat', { agentKey });
  const [busy, setBusy] = useState(false);
  const pending = chat.data?.turns.some(waiting) ?? false;
  // Check back while an answer is on its way.
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => void chat.reload(), POLL_MS);
    return () => clearInterval(t);
  }, [pending, chat.reload]);
  // a refusal says why in the box's red line (Composer keeps the words)
  const ask = async (question: string) => {
    setBusy(true);
    try {
      await client.call(agentKey === 'analyst' ? 'agents.askAnalyst' : 'agents.askSupport', { question });
      await chat.reload();
    } catch (e) {
      throw new Error(errorText(e), { cause: e });
    } finally {
      setBusy(false);
    }
  };
  // an example asked from its link says a refusal in the place's status line
  const askExample = (q: string) => void ask(q).catch((e: Error) => props.toast(e.message));
  return (
    <>
      {head ? (
        <PageHead {...head} />
      ) : (
        <div className="page-head">
          <h2 className="page-title">{title}</h2>
          <p className="muted">{intro}</p>
        </div>
      )}
      {said}
      <section
        className="agents-chat"
        data-under-head={head ? '' : undefined}
        aria-label={head ? String(head.title) : (title ?? 'Chat')}
      >
        <PartSays
          status={chat.data ? 'ready' : chat.error ? 'failed' : 'loading'}
          what="the conversation"
          failedText={chat.error ?? undefined}
          onRetry={() => void chat.reload()}
        >
          <div aria-live="polite">
            <RowList
              label="The questions asked"
              rows={(chat.data?.turns ?? []).map((t) => turnRow(t, props.timeZone))}
              empty="Nothing asked yet. Try one of the questions below."
            />
          </div>
        </PartSays>
        <p className="agents-examples">
          {examples.map((e) => (
            <Link key={e} isDisabled={busy} onPress={() => askExample(e)}>
              {e}
            </Link>
          ))}
        </p>
        <Composer
          label="Ask a question"
          sendOnEnter
          modes={[{ id: agentKey, label: 'Your question', placeholder: 'Ask a question', sendLabel: 'Ask' }]}
          onSend={(_, text) => ask(text)}
        />
      </section>
    </>
  );
}
